import { useState, useRef, useEffect } from 'react';
import { ChevronsUp, Pencil, Zap, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

function formatTokens(n: number): string {
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return String(n);
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatCost(cost: number): string {
  if (cost >= 0.01) return `$${cost.toFixed(2)}`;
  if (cost > 0) return `$${cost.toFixed(4)}`;
  return '$0.00';
}

interface ChatFloatingBarProps {
  title: string;
  onTitleChange: (title: string) => void;
  totalTokens: number;
  totalCost: number;
  totalResponseTimeMs: number;
  onCollapse?: () => void;
  className?: string;
}

export function ChatFloatingBar({
  title,
  onTitleChange,
  totalTokens,
  totalCost,
  totalResponseTimeMs,
  onCollapse,
  className,
}: ChatFloatingBarProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = () => {
    setEditing(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== title) {
      onTitleChange(trimmed);
    } else {
      setEditValue(title);
    }
  };

  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-full border bg-background px-3 py-1.5 text-xs shadow-md md:max-w-md md:gap-2.5 md:px-3.5 md:py-2',
        className
      )}
    >
      {onCollapse && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 rounded-full md:h-7 md:w-7"
          onClick={onCollapse}
          aria-label="Collapse"
        >
          <ChevronsUp className="h-3.5 w-3.5 md:h-4 md:w-4" />
        </Button>
      )}
      <div className="min-w-0 flex-1">
        {editing ? (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') {
                setEditValue(title);
                setEditing(false);
              }
            }}
            className="h-7 rounded-md text-xs font-semibold md:h-8 md:text-sm"
          />
        ) : (
          <div className="flex items-center gap-1">
            <span className="truncate text-xs font-semibold md:text-sm">
              {title || 'New conversation'}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 rounded-full md:h-7 md:w-7"
              onClick={() => setEditing(true)}
              aria-label="Edit title"
            >
              <Pencil className="h-3 w-3 text-muted-foreground md:h-3.5 md:w-3.5" />
            </Button>
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2.5 text-muted-foreground md:gap-3">
        <span className="flex items-center gap-1" title="Total tokens used">
          <Zap className="h-3.5 w-3.5 md:h-4 md:w-4" />
          <span>{formatTokens(totalTokens)}</span>
        </span>
        {totalCost > 0 && (
          <span className="flex items-center gap-1" title="Estimated cost (USD)">
            <span>{formatCost(totalCost)}</span>
          </span>
        )}
        <span className="flex items-center gap-1" title="Total AI response time">
          <Clock className="h-3.5 w-3.5 md:h-4 md:w-4" />
          <span>{formatDuration(totalResponseTimeMs)}</span>
        </span>
      </div>
    </div>
  );
}
